// Camera/microphone helpers with graceful fallback.

export interface AcquiredMedia {
  stream: MediaStream;
  hasVideo: boolean;
  hasAudio: boolean;
}

const AUDIO: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

const VIDEO: MediaTrackConstraints = {
  width: { ideal: 1280 },
  height: { ideal: 720 },
  facingMode: "user",
};

// Tries full audio+video, then degrades: a device with a mic but no camera
// still joins (audio only), and vice-versa. Permission denials are NOT retried
// (that would just re-prompt) — they bubble up for the caller to report.
export async function getLocalStream(): Promise<AcquiredMedia> {
  const attempts: MediaStreamConstraints[] = [
    { audio: AUDIO, video: VIDEO },
    { audio: AUDIO, video: false }, // mic, no camera
    { audio: false, video: VIDEO }, // camera, no mic
  ];

  let lastErr: unknown;
  for (const constraints of attempts) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      return describe(stream);
    } catch (err) {
      if (isPermissionDenied(err)) throw err;
      lastErr = err;
    }
  }
  throw lastErr;
}

function describe(stream: MediaStream): AcquiredMedia {
  return {
    stream,
    hasVideo: stream.getVideoTracks().length > 0,
    hasAudio: stream.getAudioTracks().length > 0,
  };
}

export function isPermissionDenied(err: unknown): boolean {
  const name = (err as DOMException | undefined)?.name;
  return name === "NotAllowedError" || name === "SecurityError";
}

export function setMic(stream: MediaStream, on: boolean): void {
  stream.getAudioTracks().forEach((t) => (t.enabled = on));
}

export function setCam(stream: MediaStream, on: boolean): void {
  stream.getVideoTracks().forEach((t) => (t.enabled = on));
}

export function stopStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((t) => t.stop());
}
